import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import TagUsageHistoryComponent from "./component";

const TagUsageHistoryContainer = (props) => {
  const { tagId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [usageData, setUsageData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState(""); // Temporary for input
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tempStartDate, setTempStartDate] = useState(""); // Temporary for input
  const [tempEndDate, setTempEndDate] = useState(""); // Temporary for input
  const [tagUid, setTagUid] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  // Get tag UID from params or location state
  useEffect(() => {
    if (tagId) {
      setTagUid(tagId);
    }
    if (location.state?.tagUid) {
      setTagUid(location.state.tagUid);
    }
  }, [tagId, location.state]);

  // Convert UTC to IST and extract date only (YYYY-MM-DD)
  const convertUTCtoISTDate = (utcDateString) => {
    if (!utcDateString) return "";
    const date = new Date(utcDateString);
    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    return istDate.toISOString().split('T')[0];
  };

  // Fetch usage history - only once when component mounts
  const fetchUsageHistory = useCallback(() => {
    if (!tagId && !tagUid) {
      console.error("No tag UID available");
      toastr.error("Error", "Tag ID not found");
      setLoading(false);
      return;
    }
    
    const uid = tagId || tagUid;
    setLoading(true);
    
    let url = `/partner/api/v2/usage-history/${uid}`;
    
    console.log("Fetching usage history for UID:", uid);
    console.log("API URL:", url);
    
    props.getDataFromAPI(
      url,
      "GET",
      undefined,
      (response) => {
        console.log("Usage History Response:", response);
        
        let data = [];
        
        if (response?.payload && Array.isArray(response.payload)) {
          data = response.payload;
        } else if (response?.payload && Array.isArray(response.payload)) {
          data = response.payload;
        } else if (Array.isArray(response)) {
          data = response;
        } else if (response && Array.isArray(response)) {
          data = response;
        }
        
        // Add IST date to each item for filtering
        const dataWithISTDate = data.map(item => ({
          ...item,
          istDate: convertUTCtoISTDate(item.timestamp)
        }));
        
        setUsageData(dataWithISTDate);
        setFilteredData(dataWithISTDate);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching usage history:", error);
        toastr.error("Error", error?.message || "Unable to fetch usage history");
        setLoading(false);
      }
    );
  }, [tagId, tagUid, props]);

  // Initial fetch - only once
  useEffect(() => {
    if (tagId || tagUid) {
      fetchUsageHistory();
    }
  }, [tagId, tagUid]);

  // Apply filters ONLY when Apply button is clicked
  const applyFilters = () => {
    if (!usageData.length) return;
    
    let filtered = [...usageData];
    
    // Apply search filter
    if (tempSearchTerm && tempSearchTerm.trim()) {
      const term = tempSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(item => 
        (item.orderId || '').toLowerCase().includes(term) ||
        (item.transactionId || '').toString().includes(term) ||
        (item.opsType || '').toLowerCase().includes(term)
      );
    }
    
    // Apply date range filter - DATE ONLY
    if (tempStartDate && tempEndDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.istDate;
        return itemDate >= tempStartDate && itemDate <= tempEndDate;
      });
    } else if (tempStartDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.istDate;
        return itemDate === tempStartDate;
      });
    } else if (tempEndDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.istDate;
        return itemDate === tempEndDate;
      });
    }
    
    // Update actual filter values and filtered data
    setSearchTerm(tempSearchTerm);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setFilteredData(filtered);
    setIsFilterApplied(true);
  };

  // Clear all filters
  const clearFilters = () => {
    setTempSearchTerm("");
    setTempStartDate("");
    setTempEndDate("");
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setFilteredData(usageData);
    setIsFilterApplied(false);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleSearchChange = (value) => {
    setTempSearchTerm(value);
    // Don't apply filter immediately, wait for Apply button
  };

  const handleSearch = () => {
    applyFilters();
  };

  const handleStartDateChange = (date) => {
    setTempStartDate(date);
    // Don't apply filter immediately, wait for Apply button
  };

  const handleEndDateChange = (date) => {
    setTempEndDate(date);
    // Don't apply filter immediately, wait for Apply button
  };

  const handleDateFilter = () => {
    applyFilters();
  };

  const handleClearDateFilter = () => {
    clearFilters();
  };

  return (
    <TagUsageHistoryComponent
      tagId={tagId}
      tagUid={tagUid}
      loading={loading}
      usageData={filteredData}
      originalDataLength={usageData.length}
      searchTerm={tempSearchTerm}
      onSearchChange={handleSearchChange}
      onSearch={handleSearch}
      startDate={tempStartDate}
      endDate={tempEndDate}
      onStartDateChange={handleStartDateChange}
      onEndDateChange={handleEndDateChange}
      onDateFilter={handleDateFilter}
      onClearDateFilter={handleClearDateFilter}
      onBackClick={handleBackClick}
      isFilterApplied={isFilterApplied}
    />
  );
};

function mapStateToProps(state) {
  return {
    props: state,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(TagUsageHistoryContainer);